import React, { createContext, useContext, useRef, useState, useEffect, useCallback } from 'react';
import { getAudioUrl, baseURL, apiBaseURL } from '../constants/routes';
import { useAuthProvider } from '../utils/auth';
import { useStreamingQuality } from './useStreamingQuality';
import { storageSync } from '../utils/storage';
import { canPlayOffline, getOfflineUrl, getSaved, recordOfflinePlay, syncOfflinePlays } from '../utils/offlineLibrary';

const MusicPlayerContext = createContext();

export const useMusicPlayer = () => {
  const context = useContext(MusicPlayerContext);
  if (!context) {
    throw new Error('useMusicPlayer must be used within a MusicPlayerProvider');
  }
  return context;
};

// A stream can be ended from several places (pause, track change, seek,
// leaving the page) that sometimes fire together. The server rightly
// rejects a second end with 400, so every caller for a given stream shares
// the first request; each gets its own clone of the response to read.
const streamEndRequests = new Map();
const fetchStreamEnd = (streamId, init) => {
  // A saved copy played while offline (see startOfflineSession): queue the
  // play for the server instead of ending a session it never started
  if (String(streamId).startsWith('offline:') && !streamEndRequests.has(streamId)) {
    const [, trackId, startedAt, userId] = String(streamId).split(':');
    let durationSeconds = 0;
    try {
      durationSeconds = JSON.parse(init.body).duration_seconds;
    } catch (e) {
      // no duration - nothing to record
    }
    recordOfflinePlay({
      track_id: Number(trackId),
      played_at: new Date(Number(startedAt)).toISOString(),
      duration_seconds: durationSeconds,
    }, userId);
    syncOfflinePlays(userId);
    streamEndRequests.set(streamId, Promise.resolve(new Response(null, { status: 204 })));
  }
  if (!streamEndRequests.has(streamId)) {
    streamEndRequests.set(streamId, fetch(`${apiBaseURL}/music/track/listening/${streamId}/end/`, init));
  }
  return streamEndRequests.get(streamId).then((response) => response.clone());
};

export const MusicPlayerProvider = ({ children }) => {
  const audioRef = useRef((() => {
    const audio = new Audio();
    // Configure audio element for better handling of large files
    audio.preload = 'metadata'; // Only load metadata initially, not the entire file
    audio.crossOrigin = 'anonymous'; // Enable CORS
    return audio;
  })());
  const { isActListeningMode, currentActId, isAuthed, user } = useAuthProvider();
  const preloadRef = useRef(new Audio());
  // The object URL of a saved copy being played, revoked when replaced
  const offlineUrlRef = useRef(null);

  // Send plays made offline once there's a connection
  useEffect(() => {
    const sync = () => syncOfflinePlays(user?.uuid);
    sync();
    window.addEventListener('online', sync);
    return () => window.removeEventListener('online', sync);
  }, [user?.uuid]);
  
  // State variables must be declared before useEffects
  const [currentTrack, setCurrentTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.7);
  const [playlist, setPlaylist] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [playHistory, setPlayHistory] = useState([]);
  const [lastPlayedState, setLastPlayedState] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentStreamId, setCurrentStreamId] = useState(null);
  const [streamStartTime, setStreamStartTime] = useState(null);
  const [listeningTimeRefresh, setListeningTimeRefresh] = useState(0);
  const [queue, setQueue] = useState([]); // Add queue state for upcoming tracks
  const [isShuffleOn, setIsShuffleOn] = useState(false);
  const [repeatMode, setRepeatMode] = useState('off'); // 'off', 'one', 'all'
  const [originalPlaylist, setOriginalPlaylist] = useState([]); // Store original playlist order for shuffle
  const [creditWarningDialog, setCreditWarningDialog] = useState({
    open: false,
    trackName: '',
    requiredAmount: '0.01'
  });
  const [isPausing, setIsPausing] = useState(false); // Prevent rapid pause clicks
  const pauseTimeoutRef = useRef(null); // Track pending pause operations
  
  // Show credit warning dialog - defined early so it can be used in useCallback hooks
  const showCreditWarning = useCallback((trackName, requiredAmount) => {
    console.log('🚨 Showing credit warning dialog:', { trackName, requiredAmount });
    setCreditWarningDialog({
      open: true,
      trackName,
      requiredAmount
    });
  }, []);
  
  // Configure audio elements for faster playback
  useEffect(() => {
    const audio = audioRef.current;
    const preloadAudio = preloadRef.current;
    
    // Set audio properties for faster buffering
    audio.preload = 'auto';
    audio.crossOrigin = 'anonymous';
    audio.volume = 0.7;
    
    // Add additional properties to help with loading
    audio.controls = false;
    audio.muted = false;
    audio.loop = false;
    
    preloadAudio.preload = 'auto';
    preloadAudio.crossOrigin = 'anonymous';
    preloadAudio.volume = 0; // Silent preloading
    preloadAudio.controls = false;
    preloadAudio.muted = true;
    preloadAudio.loop = false;
    
    // Add error handling for preload audio
    const handlePreloadError = (event) => {
      console.warn('Preload audio error:', event);
      console.warn('Preload audio src:', preloadAudio.src);
    };
    
    preloadAudio.addEventListener('error', handlePreloadError);
    
    // Add event listeners for listening tracking
    const handleCanPlay = () => setIsLoading(false);
    const handleLoadStart = () => setIsLoading(true);
    const handleEnded = () => {
      setIsPlaying(false);
      // End listening tracking when track ends naturally
      // This will be handled by the pause function
    };
    
    const handleError = (event) => {
      console.error('Audio playback error:', event);
      console.error('Audio element:', audio);
      console.error('Audio src:', audio.src);
      console.error('Audio readyState:', audio.readyState);
      setIsLoading(false);
      setIsPlaying(false);
    };
    
    // Add buffering event handlers to detect and log buffering issues
    let waitingCount = 0;
    const handleWaiting = () => {
      waitingCount++;
      console.warn(`🎵 Audio buffering (waiting event #${waitingCount}) - buffer may be low`);
      // If buffering happens frequently, it indicates network/buffering issues
      if (waitingCount > 5) {
        console.warn('🎵 Frequent buffering detected - may indicate network issues or insufficient buffer');
      }
    };
    
    const handleStalled = () => {
      console.warn('🎵 Audio stalled - network issue detected');
    };
    
    const handleCanPlayThrough = () => {
      // Reset waiting count when buffer is sufficient
      waitingCount = 0;
    };
    
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('loadstart', handleLoadStart);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);
    audio.addEventListener('waiting', handleWaiting);
    audio.addEventListener('stalled', handleStalled);
    audio.addEventListener('canplaythrough', handleCanPlayThrough);
    
    console.log('MusicPlayerProvider initialized with optimized audio settings');
    
    // Cleanup event listeners
    return () => {
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('loadstart', handleLoadStart);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('waiting', handleWaiting);
      audio.removeEventListener('stalled', handleStalled);
      audio.removeEventListener('canplaythrough', handleCanPlayThrough);
      preloadAudio.removeEventListener('error', handlePreloadError);
    };
  }, []);

  // Quality preference, buffering state and step-down suggestions
  const streamingQualityState = useStreamingQuality({ audioRef, currentTrack, isAuthed });

  // Preload the next track in the playlist for seamless playback
  const preloadNextTrack = useCallback(async (currentIndex) => {
    if (!playlist || currentIndex < 0 || currentIndex >= playlist.length - 1) {
      return; // No next track to preload
    }
    
    const nextTrack = playlist[currentIndex + 1];
    if (!nextTrack) {
      return;
    }

    const preloadAudio = preloadRef.current;

    // Only a track's owner gets its file address in advance. Everyone else's
    // comes with the listening session when the track starts (starting one
    // early would charge for it), so there's nothing to preload for them.
    const directUrl = nextTrack.audio_file ? getAudioUrl(nextTrack.audio_file) : null;
    if (directUrl) {
      preloadAudio.src = directUrl;
      preloadAudio.load(); // Start buffering immediately
    }
  }, [playlist]);

  // Play a specific track by index or track object
  const playTrack = useCallback(async (trackOrIndex, discoveryContext = {}) => {
    let track;
    let index;

    if (typeof trackOrIndex === 'number') {
      index = trackOrIndex;
      track = playlist[index];
    } else {
      track = trackOrIndex;
      index = playlist.findIndex(t => t.id === track.id);
      if (index === -1) {
        // Add track to playlist if not found
        setPlaylist(prev => [...prev, track]);
        index = playlist.length;
      }
    }

    // Tracks queued from a playlist (or other source) carry how they were
    // found, so the curator is credited for every track in the queue - not
    // just the first one, which is the only call that passes a context
    const playContext = discoveryContext && Object.keys(discoveryContext).length > 0
      ? discoveryContext
      : (track.discoveryContext || {});

    // Check for audio_file in multiple possible locations. This is normally
    // absent now - the catalog/browse API only includes a direct audio_file
    // URL for the track's owner. Everyone else gets their playable URL below
    // with the listening session (start_listening_session returns it), so we
    // must NOT bail out here just because this field is empty.
    const audioFile = track.audio_file || track.audioFile;

    // Check for private link token in URL or sessionStorage (for unauthenticated streaming)
    const searchParams = new URLSearchParams(window.location.search);
    let linkToken = searchParams.get('link_token');
    
    // If not in URL, check sessionStorage for private album/track tokens
    if (!linkToken) {
      // Check all sessionStorage keys for private link tokens
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && (key.startsWith('private_album_token_') || key.startsWith('private_track_token_'))) {
          linkToken = sessionStorage.getItem(key);
          if (linkToken) break;
        }
      }
    }

    // OPTIMIZATION: Start audio URL preparation in parallel with credit check
    // This allows us to prepare everything while credit is being verified
    // A copy saved on this device plays without streaming (see offlineLibrary)
    const offlineUrl = await getOfflineUrl(track.id, user?.uuid);
    const startOfflineSession = () => {
      setCurrentStreamId(`offline:${track.id}:${Date.now()}:${user?.uuid || ''}`);
      setStreamStartTime(new Date());
    };
    const directAudioUrl = getAudioUrl(audioFile);
    // Where to stream from comes with the listening session
    let sessionAudioUrl = null;
    
    // Check credit before starting playback (or verify private link access)
    try {
      const token = storageSync.getItem('token');
      
      // If authenticated, use normal flow
      if (token) {
        const requestBody = { 
          track_id: track.id,
          ...playContext
        };
        
        // Add act_id if in act listening mode
        if (isActListeningMode && currentActId) {
          requestBody.act_id = currentActId;
        }
        
        const response = await fetch(`${apiBaseURL}/music/track/listening/start/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${token}`,
          },
          body: JSON.stringify(requestBody),
        });

        if (!response.ok) {
          if (response.status === 402) {
            // Payment required - insufficient credit
            const errorData = await response.json();
            console.error('🚨 Insufficient credit to play track:', track.name);
            showCreditWarning(track.name, String(errorData.track_price_per_minute || '0.01'));
            return;
          } else {
            console.error('Failed to start listening session:', response.status, response.statusText);
            return;
          }
        }

        // If we get here, credit check passed, get the stream data
        const streamData = await response.json();
        sessionAudioUrl = streamData.audio_url ? getAudioUrl(streamData.audio_url) : null;
        setCurrentStreamId(streamData.stream_id);
        setStreamStartTime(new Date());
      } else if (linkToken) {
        // Unauthenticated but has private link token - allow free streaming
        const requestBody = { 
          track_id: track.id,
          link_token: linkToken,
          ...playContext
        };
        
        const response = await fetch(`${apiBaseURL}/music/track/listening/start/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          console.error('🚨 Failed to start private link listening session:', response.status);
          alert(errorData.error || 'Failed to start playback. Please check your private link.');
          return;
        }

        // Private link access granted - get the stream data
        const streamData = await response.json();
        sessionAudioUrl = streamData.audio_url ? getAudioUrl(streamData.audio_url) : null;
        setCurrentStreamId(streamData.stream_id);
        setStreamStartTime(new Date());
      } else {
        // No auth token and no private link token: block playback instead of
        // silently falling through to directAudioUrl. Login/credit is required
        // to stream - this is not reachable via a free "preview" path.
        console.warn('No auth token or private link token found; blocking playback for', track.name);
        showCreditWarning(track.name, '0.01');
        return;
      }
    } catch (error) {
      // No connection: a saved copy still plays, and the play is sent later.
      // Music they don't own only while their last known balance covers it.
      if (!offlineUrl) {
        console.error('Error checking credit for track:', error);
        return;
      }
      if (!canPlayOffline(getSaved(track.id, user?.uuid), user?.uuid)) {
        URL.revokeObjectURL(offlineUrl);
        alert("Connect to the internet to keep listening - you've used the credit this device knew about, or it's been more than 30 days since it checked in.");
        return;
      }
      startOfflineSession();
    }

      // End any existing listening session
      if (currentStreamId && streamStartTime) {
        try {
          const token = storageSync.getItem('token');
          const searchParams = new URLSearchParams(window.location.search);
          let linkToken = searchParams.get('link_token');
          
          // If not in URL, check sessionStorage for private album/track tokens
          if (!linkToken) {
            for (let i = 0; i < sessionStorage.length; i++) {
              const key = sessionStorage.key(i);
              if (key && (key.startsWith('private_album_token_') || key.startsWith('private_track_token_'))) {
                linkToken = sessionStorage.getItem(key);
                if (linkToken) break;
              }
            }
          }
        
        // Calculate duration in seconds
        const durationSeconds = Math.floor((Date.now() - streamStartTime) / 1000);
        
        if (token) {
          // Authenticated user
          await fetchStreamEnd(currentStreamId, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Token ${token}`,
            },
            body: JSON.stringify({
              duration_seconds: durationSeconds
            }),
          });
        } else if (linkToken) {
          // Private link access - no auth needed
          await fetchStreamEnd(currentStreamId, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              'X-Private-Link-Token': linkToken,
            },
            body: JSON.stringify({
              duration_seconds: durationSeconds
            }),
          });
        }
      } catch (error) {
        console.error('Error ending listening tracking:', error);
      }
      setCurrentStreamId(null);
      setStreamStartTime(null);
    }

    // Add to play history
    setPlayHistory(prev => {
      const newHistory = prev.filter(item => item.id !== track.id);
      return [{
        ...track,
        playedAt: Date.now(),
        playlist: playlist,
        playlistIndex: index
      }, ...newHistory].slice(0, 50); // Keep last 50 tracks
    });

    setIsLoading(true);
    setCurrentTrack(track);
    setCurrentIndex(index);
    
    // A saved copy, the session's stream address, or (for the track's
    // owner) its file
    const finalAudioUrl = offlineUrl || sessionAudioUrl || directAudioUrl;

    if (!finalAudioUrl) {
      console.error('No playable audio URL available for track:', track.id, track.name);
      alert('This track is not available for playback. The audio file may not be uploaded yet.');
      setIsLoading(false);
      return;
    }

    // Clear any existing source first
    // Clean up any previous buffer monitoring
    if (audioRef.current._bufferCheckCleanup) {
      audioRef.current._bufferCheckCleanup();
      audioRef.current._bufferCheckCleanup = null;
    }
    
    audioRef.current.src = '';
    audioRef.current.load();
    
    if (offlineUrlRef.current && offlineUrlRef.current !== finalAudioUrl) {
      URL.revokeObjectURL(offlineUrlRef.current);
    }
    offlineUrlRef.current = finalAudioUrl.startsWith('blob:') ? finalAudioUrl : null;

    // Start loading audio immediately
    audioRef.current.src = finalAudioUrl;
    audioRef.current.load(); // Start buffering immediately
    
    // Detect if this is a cloud storage URL (external URL)
    const isCloudStorageUrl = finalAudioUrl && (
      finalAudioUrl.includes('linodeobjects.com') ||
      finalAudioUrl.includes('amazonaws.com') ||
      finalAudioUrl.includes('s3') ||
      (finalAudioUrl.startsWith('http') && !finalAudioUrl.includes(window.location.hostname))
    );
    
    // Wait for audio to be ready - use appropriate buffering strategy
    const waitForAudioReady = () => {
      return new Promise((resolve, reject) => {
        const audio = audioRef.current;
        
        // Check if already ready (e.g., from preloading)
        // For cloud storage, require more buffer (readyState >= 3 = HAVE_FUTURE_DATA)
        const minReadyState = isCloudStorageUrl ? 3 : 2;
        if (audio.readyState >= minReadyState) {
          resolve();
          return;
        }
        
        // Helper function to check if we have sufficient buffer
        const hasSufficientBuffer = () => {
          if (!audio.buffered || audio.buffered.length === 0) {
            return false;
          }
          
          const bufferedEnd = audio.buffered.end(audio.buffered.length - 1);
          const currentTime = audio.currentTime || 0;
          const bufferedAhead = bufferedEnd - currentTime;
          
          // For cloud storage, require at least 15 seconds of buffer
          // For local storage, require at least 5 seconds
          const minBufferSeconds = isCloudStorageUrl ? 15 : 5;
          
          return bufferedAhead >= minBufferSeconds;
        };
        
        // For cloud storage, wait for 'canplaythrough' AND sufficient buffer
        // For local storage, can use 'loadeddata' for faster start
        const handleCanPlayThrough = () => {
          // Even after canplaythrough, verify we have enough buffer
          if (isCloudStorageUrl) {
            // Check buffered ranges - wait for at least 15 seconds
            const checkBuffer = () => {
              if (hasSufficientBuffer()) {
                clearTimeout(timeoutId);
                audio.removeEventListener('canplaythrough', handleCanPlayThrough);
                audio.removeEventListener('progress', handleProgress);
                audio.removeEventListener('loadeddata', handleLoadedData);
                audio.removeEventListener('canplay', handleCanPlay);
                audio.removeEventListener('error', handleError);
                audio.removeEventListener('waiting', handleWaiting);
                audio.removeEventListener('stalled', handleStalled);
                resolve();
              } else {
                // Keep checking on progress events
                // Don't resolve yet - wait for more buffer
              }
            };
            
            // Check immediately
            checkBuffer();
            
            // Also listen to progress events for buffer updates
            const handleProgress = () => {
              checkBuffer();
            };
            audio.addEventListener('progress', handleProgress);
            
            // Set a timeout to proceed even if buffer isn't perfect (after 20s)
            const bufferTimeout = setTimeout(() => {
              console.warn('🎵 Proceeding with playback despite limited buffer');
              clearTimeout(timeoutId);
              audio.removeEventListener('canplaythrough', handleCanPlayThrough);
              audio.removeEventListener('progress', handleProgress);
              audio.removeEventListener('loadeddata', handleLoadedData);
              audio.removeEventListener('canplay', handleCanPlay);
              audio.removeEventListener('error', handleError);
              audio.removeEventListener('waiting', handleWaiting);
              audio.removeEventListener('stalled', handleStalled);
              resolve();
            }, 20000);
            
            // Store handleProgress and bufferTimeout for cleanup
            handleCanPlayThrough._handleProgress = handleProgress;
            handleCanPlayThrough._bufferTimeout = bufferTimeout;
            
            return; // Don't resolve yet - wait for buffer check
          } else {
            // For local storage, canplaythrough is sufficient
            clearTimeout(timeoutId);
            audio.removeEventListener('canplaythrough', handleCanPlayThrough);
            audio.removeEventListener('loadeddata', handleLoadedData);
            audio.removeEventListener('canplay', handleCanPlay);
            audio.removeEventListener('error', handleError);
            audio.removeEventListener('waiting', handleWaiting);
            audio.removeEventListener('stalled', handleStalled);
            resolve();
          }
        };
        
        const handleLoadedData = () => {
          // Only resolve on loadeddata for local storage
          if (!isCloudStorageUrl) {
            clearTimeout(timeoutId);
            audio.removeEventListener('canplaythrough', handleCanPlayThrough);
            audio.removeEventListener('loadeddata', handleLoadedData);
            audio.removeEventListener('canplay', handleCanPlay);
            audio.removeEventListener('error', handleError);
            audio.removeEventListener('waiting', handleWaiting);
            audio.removeEventListener('stalled', handleStalled);
            resolve();
          }
        };
        
        const handleCanPlay = () => {
          // For cloud storage, wait for canplaythrough instead
          if (!isCloudStorageUrl) {
            clearTimeout(timeoutId);
            audio.removeEventListener('canplaythrough', handleCanPlayThrough);
            audio.removeEventListener('loadeddata', handleLoadedData);
            audio.removeEventListener('canplay', handleCanPlay);
            audio.removeEventListener('error', handleError);
            audio.removeEventListener('waiting', handleWaiting);
            audio.removeEventListener('stalled', handleStalled);
            resolve();
          }
        };
        
        // Track buffering issues
        let waitingCount = 0;
        const handleWaiting = () => {
          waitingCount++;
          console.warn(`🎵 Audio buffering (waiting event #${waitingCount})`);
          // If we get too many waiting events, something is wrong
          if (waitingCount > 10) {
            console.error('🎵 Too many buffering events, audio may be having network issues');
          }
        };
        
        const handleStalled = () => {
          console.warn('🎵 Audio stalled - network issue detected');
        };
        
        const handleError = (error) => {
          clearTimeout(timeoutId);
          // Clean up any progress listeners
          if (handleCanPlayThrough._handleProgress) {
            audio.removeEventListener('progress', handleCanPlayThrough._handleProgress);
          }
          if (handleCanPlayThrough._bufferTimeout) {
            clearTimeout(handleCanPlayThrough._bufferTimeout);
          }
          
          // Handle 404 errors gracefully
          if (audio.error?.code === 2) {
            console.warn('🎵 Audio file not found (404). Skipping this track.');
            audio.removeEventListener('canplaythrough', handleCanPlayThrough);
            audio.removeEventListener('loadeddata', handleLoadedData);
            audio.removeEventListener('canplay', handleCanPlay);
            audio.removeEventListener('error', handleError);
            audio.removeEventListener('waiting', handleWaiting);
            audio.removeEventListener('stalled', handleStalled);
            reject(new Error('Audio file not found - skipping track'));
            return;
          }
          
          // Log other errors with minimal details
          if (audio.error) {
            const errorMessages = {
              1: 'MEDIA_ERR_ABORTED',
              2: 'MEDIA_ERR_NETWORK',
              3: 'MEDIA_ERR_DECODE',
              4: 'MEDIA_ERR_SRC_NOT_SUPPORTED'
            };
            console.error('🎵 Audio error:', errorMessages[audio.error.code] || `Unknown error (${audio.error.code})`);
          } else {
            console.error('🎵 Audio error: Unknown error');
          }
          
          audio.removeEventListener('canplaythrough', handleCanPlayThrough);
          audio.removeEventListener('loadeddata', handleLoadedData);
          audio.removeEventListener('canplay', handleCanPlay);
          audio.removeEventListener('error', handleError);
          audio.removeEventListener('waiting', handleWaiting);
          audio.removeEventListener('stalled', handleStalled);
          reject(error);
        };
        
        // Longer timeout for cloud storage (45s) vs local (30s)
        const timeoutDuration = isCloudStorageUrl ? 45000 : 30000;
        const timeoutId = setTimeout(() => {
          console.error(`🎵 Audio loading timeout after ${timeoutDuration / 1000} seconds`);
          // Clean up any progress listeners
          if (handleCanPlayThrough._handleProgress) {
            audio.removeEventListener('progress', handleCanPlayThrough._handleProgress);
          }
          if (handleCanPlayThrough._bufferTimeout) {
            clearTimeout(handleCanPlayThrough._bufferTimeout);
          }
          audio.removeEventListener('canplaythrough', handleCanPlayThrough);
          audio.removeEventListener('loadeddata', handleLoadedData);
          audio.removeEventListener('canplay', handleCanPlay);
          audio.removeEventListener('error', handleError);
          audio.removeEventListener('waiting', handleWaiting);
          audio.removeEventListener('stalled', handleStalled);
          reject(new Error('Audio loading timeout'));
        }, timeoutDuration);
        
        // Listen to appropriate events based on storage type
        if (isCloudStorageUrl) {
          // For cloud storage, wait for canplaythrough to ensure sufficient buffer
          audio.addEventListener('canplaythrough', handleCanPlayThrough, { once: true });
        } else {
          // For local storage, can start earlier
          audio.addEventListener('loadeddata', handleLoadedData, { once: true });
          audio.addEventListener('canplay', handleCanPlay, { once: true });
        }
        audio.addEventListener('error', handleError, { once: true });
        audio.addEventListener('waiting', handleWaiting);
        audio.addEventListener('stalled', handleStalled);
        
      });
    };
    
    // Wait for audio to be ready, then play
    try {
      await waitForAudioReady();
      const playPromise = audioRef.current.play();
    
      if (playPromise !== undefined) {
        playPromise
            .then(async () => {
              setIsPlaying(true);
              setIsLoading(false);
              // Store playing state for credit balance polling optimization
              if (typeof window !== 'undefined') {
                localStorage.setItem('musicPlayer_isPlaying', 'true');
              }
              // Preload next track
              preloadNextTrack(index);
              
              // Update Media Session playback state
              if ('mediaSession' in navigator) {
                navigator.mediaSession.playbackState = 'playing';
              }
              
              // Monitor buffer during playback for cloud storage
              if (isCloudStorageUrl) {
                const audio = audioRef.current;
                let bufferCheckInterval = null;
                
                let isBufferingPaused = false;
                let bufferRecoveryTimeout = null;
                
                const checkBufferDuringPlayback = () => {
                  // Don't check if already paused by user or if not playing
                  if (audio.paused && !isBufferingPaused) {
                    return;
                  }
                  
                  if (!audio.buffered || audio.buffered.length === 0) {
                    return;
                  }
                  
                  const bufferedEnd = audio.buffered.end(audio.buffered.length - 1);
                  const currentTime = audio.currentTime || 0;
                  const bufferedAhead = bufferedEnd - currentTime;
                  
                  // If buffer drops below 5 seconds and we're playing, proactively pause
                  if (bufferedAhead < 5 && !audio.paused && !isBufferingPaused) {
                    console.warn(`🎵 Buffer low (${bufferedAhead.toFixed(1)}s ahead), pausing to buffer more`);
                    isBufferingPaused = true;
                    audio.pause();
                    setIsLoading(true);
                    
                    // Wait for buffer to recover
                    const waitForBuffer = () => {
                      if (!audio.buffered || audio.buffered.length === 0) {
                        return;
                      }
                      
                      const newBufferedEnd = audio.buffered.end(audio.buffered.length - 1);
                      const newBufferedAhead = newBufferedEnd - audio.currentTime;
                      
                      // Resume when we have at least 10 seconds of buffer
                      if (newBufferedAhead >= 10) {
                        console.log(`🎵 Buffer recovered (${newBufferedAhead.toFixed(1)}s ahead), resuming playback`);
                        isBufferingPaused = false;
                        if (bufferRecoveryTimeout) {
                          clearTimeout(bufferRecoveryTimeout);
                          bufferRecoveryTimeout = null;
                        }
                        audio.removeEventListener('progress', waitForBuffer);
                        audio.removeEventListener('canplaythrough', waitForBuffer);
                        audio.play().catch(err => {
                          console.error('Failed to resume playback:', err);
                          setIsLoading(false);
                        });
                        setIsLoading(false);
                      }
                    };
                    
                    audio.addEventListener('progress', waitForBuffer);
                    audio.addEventListener('canplaythrough', waitForBuffer);
                    
                    // Resume after 15 seconds even if buffer isn't perfect
                    bufferRecoveryTimeout = setTimeout(() => {
                      audio.removeEventListener('progress', waitForBuffer);
                      audio.removeEventListener('canplaythrough', waitForBuffer);
                      if (isBufferingPaused && audio.paused) {
                        console.warn('🎵 Resuming playback despite limited buffer');
                        isBufferingPaused = false;
                        audio.play().catch(err => {
                          console.error('Failed to resume playback:', err);
                          setIsLoading(false);
                        });
                        setIsLoading(false);
                      }
                      bufferRecoveryTimeout = null;
                    }, 15000);
                  } else if (bufferedAhead >= 10 && isBufferingPaused) {
                    // Buffer recovered, resume if we were paused for buffering
                    console.log(`🎵 Buffer sufficient (${bufferedAhead.toFixed(1)}s ahead), resuming playback`);
                    isBufferingPaused = false;
                    if (bufferRecoveryTimeout) {
                      clearTimeout(bufferRecoveryTimeout);
                      bufferRecoveryTimeout = null;
                    }
                    audio.play().catch(err => {
                      console.error('Failed to resume playback:', err);
                      setIsLoading(false);
                    });
                    setIsLoading(false);
                  }
                };
                
                // Check buffer every 2 seconds during playback
                bufferCheckInterval = setInterval(checkBufferDuringPlayback, 2000);
                
                // Clean up interval when track ends or changes
                const cleanupBufferCheck = () => {
                  if (bufferCheckInterval) {
                    clearInterval(bufferCheckInterval);
                    bufferCheckInterval = null;
                  }
                  if (bufferRecoveryTimeout) {
                    clearTimeout(bufferRecoveryTimeout);
                    bufferRecoveryTimeout = null;
                  }
                  isBufferingPaused = false;
                };
                
                audio.addEventListener('ended', cleanupBufferCheck, { once: true });
                
                // Store cleanup function for when track changes
                if (!audio._bufferCheckCleanup) {
                  audio._bufferCheckCleanup = cleanupBufferCheck;
                }
              }
              
              // Listening session already started in credit check above
            })
          .catch(error => {
            console.error('Failed to play audio:', error);
            setIsPlaying(false);
            setIsLoading(false);
          });
      }
    } catch (error) {
      console.error('Failed to load audio:', error);
      setIsPlaying(false);
      setIsLoading(false);
      
      // Try to retry with a different approach
      console.log('🎵 Attempting audio retry...');
      try {
        // Clear and retry with a small delay
        audioRef.current.src = '';
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Reuse the already-resolved finalAudioUrl (from the gated streaming
        // endpoint), not track.audio_file - that's only populated for the
        // track's owner and would be null here for an ordinary listener.
        console.log('🎵 Retry audio source:', finalAudioUrl);
        audioRef.current.src = finalAudioUrl;
        audioRef.current.load();
        
        // Wait a bit and try to play
        await new Promise(resolve => setTimeout(resolve, 2000));
        if (audioRef.current.readyState >= 2) { // HAVE_CURRENT_DATA or higher
          const retryPlayPromise = audioRef.current.play();
          if (retryPlayPromise !== undefined) {
              retryPlayPromise
                .then(() => {
                  console.log('🎵 Audio retry successful');
                  setIsPlaying(true);
                  setIsLoading(false);
                  preloadNextTrack(index);
                  
                  // Update Media Session playback state
                  if ('mediaSession' in navigator) {
                    navigator.mediaSession.playbackState = 'playing';
                  }
                })
              .catch(retryError => {
                console.error('🎵 Audio retry failed:', retryError);
                setIsPlaying(false);
                setIsLoading(false);
              });
          }
        } else {
          console.error('🎵 Audio retry failed - still not ready');
          setIsPlaying(false);
          setIsLoading(false);
        }
      } catch (retryError) {
        console.error('🎵 Audio retry error:', retryError);
        setIsPlaying(false);
        setIsLoading(false);
      }
    }
  }, [playlist, preloadNextTrack, currentStreamId, streamStartTime, currentActId, isActListeningMode, showCreditWarning, user?.uuid]);

  // Trigger listening time refresh
  const refreshListeningTime = useCallback(() => {
    setListeningTimeRefresh(prev => prev + 1);
  }, []);

  // Load persisted state from localStorage on mount
  useEffect(() => {
    try {
      const savedVolume = storageSync.getItem('musicPlayer_volume');
      const savedLastPlayed = storageSync.getItem('musicPlayer_lastPlayed');
      const savedPlayHistory = storageSync.getItem('musicPlayer_playHistory');
      
      if (savedVolume) {
        setVolume(parseFloat(savedVolume));
      }
      
      if (savedLastPlayed) {
        const lastPlayed = JSON.parse(savedLastPlayed);
        setLastPlayedState(lastPlayed);
        // Set current track to last played (but don't auto-play)
        if (lastPlayed.track) {
          // Validate that the track has required fields before restoring.
          // audio_file is NOT required here - the catalog API only includes
          // it for the track's owner, so it's normally absent. The real URL
          // gets resolved via the gated endpoint when resumeFromLastPosition
          // actually runs (on the user's play click), not eagerly here.
          if (lastPlayed.track.id && lastPlayed.track.name) {
            setCurrentTrack(lastPlayed.track);
            setPlaylist(lastPlayed.playlist || []);
            setCurrentIndex(lastPlayed.currentIndex || 0);
            setProgress(lastPlayed.progress || 0);

            // Only eagerly preload the audio element when we already have a
            // direct URL (i.e. for the track's owner). Otherwise the real URL
            // is resolved lazily when the user clicks play/resume.
            if (lastPlayed.track.audio_file && audioRef.current) {
              const audioUrl = getAudioUrl(lastPlayed.track.audio_file);
              audioRef.current.src = audioUrl;
              audioRef.current.currentTime = lastPlayed.progress || 0;
              console.log('🎵 Restored audio state from localStorage - track:', lastPlayed.track.name, 'position:', lastPlayed.progress, 'seconds');
            }
          } else {
            console.warn('🎵 Invalid track data in localStorage, clearing saved state');
            storageSync.removeItem('musicPlayer_lastPlayed');
            setLastPlayedState(null);
          }
        }
      }
      
      if (savedPlayHistory) {
        setPlayHistory(JSON.parse(savedPlayHistory));
      }
    } catch (error) {
      console.error('Error loading persisted music player state:', error);
    }
  }, []);

  // Save volume to localStorage when it changes
  useEffect(() => {
    storageSync.setItem('musicPlayer_volume', volume.toString());
  }, [volume]);

  // Save play history to localStorage when it changes
  useEffect(() => {
    if (playHistory.length > 0) {
      storageSync.setItem('musicPlayer_playHistory', JSON.stringify(playHistory));
    }
  }, [playHistory]);

  // Save last played state to localStorage when track changes
  useEffect(() => {
    if (currentTrack) {
      const lastPlayed = {
        track: currentTrack,
        playlist: playlist,
        currentIndex: currentIndex,
        progress: progress,
        timestamp: Date.now()
      };
      setLastPlayedState(lastPlayed);
      storageSync.setItem('musicPlayer_lastPlayed', JSON.stringify(lastPlayed));
    }
  }, [currentTrack, playlist, currentIndex, progress]);

  // Media Session API - Update metadata when track changes
  useEffect(() => {
    if ('mediaSession' in navigator && currentTrack) {
      try {
        // Get artist name from track (could be act name or creator name)
        const artist = currentTrack.act_name || 
                      currentTrack.creator_name || 
                      currentTrack.label_name || 
                      'Unknown Artist';
        
        // Get album art if available
        const artwork = [];
        // The track's own artwork, else its album's
        const cover = currentTrack.cover_art || currentTrack.image || currentTrack.album_artwork;
        if (cover) {
          const coverArtUrl = getAudioUrl(cover);
          artwork.push({
            src: coverArtUrl,
            sizes: '512x512',
            type: 'image/jpeg'
          });
        } else if (currentTrack.profile_picture) {
          // Fallback to profile picture if no cover art
          const profilePicUrl = getAudioUrl(currentTrack.profile_picture);
          artwork.push({
            src: profilePicUrl,
            sizes: '512x512',
            type: 'image/jpeg'
          });
        }
        
        navigator.mediaSession.metadata = new MediaMetadata({
          title: currentTrack.name || 'Unknown Track',
          artist: artist,
          album: currentTrack.album_name || '',
          artwork: artwork
        });
        
        console.log('🎵 Media Session metadata updated:', {
          title: currentTrack.name,
          artist: artist
        });
      } catch (error) {
        console.warn('Failed to update Media Session metadata:', error);
      }
    }
  }, [currentTrack]);




  // Set up audio event listeners
  useEffect(() => {
    const audio = audioRef.current;
    
    const updateProgress = () => {
      setProgress(audio.currentTime);
    };

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
      setIsLoading(false);
    };

    const handleCanPlay = () => {
      setIsLoading(false);
    };

    const handleLoadStart = () => {
      setIsLoading(true);
    };

    const handleEnded = async () => {
      setIsPlaying(false);
      setProgress(0);
      
      // End listening session when track finishes naturally
      if (currentStreamId && streamStartTime) {
        try {
          const token = storageSync.getItem('token');
          if (token) {
            // Calculate duration in seconds
            const durationSeconds = Math.floor((Date.now() - streamStartTime) / 1000);
            
            const response = await fetchStreamEnd(currentStreamId, {
              method: 'PATCH',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Token ${token}`,
              },
              body: JSON.stringify({
                duration_seconds: durationSeconds
              })
            });
            
            if (!response.ok) {
              console.error('Failed to end listening session:', response.status);
            }
          }
        } catch (error) {
          console.error('Error ending listening tracking on track end:', error);
        }
        setCurrentStreamId(null);
        setStreamStartTime(null);
      }
      
      // Trigger refresh of listening time data
      refreshListeningTime();
      
      // Auto-play next track - first check queue, then playlist, then repeat modes
      if (queue.length > 0) {
        // Play next queued track
        const nextTrack = queue[0];
        setQueue(prev => prev.slice(1)); // Remove from queue
        setTimeout(() => playTrack(nextTrack), 100);
      } else if (repeatMode === 'one') {
        // Repeat one: restart current track
        setTimeout(() => playTrack(currentIndex), 100);
      } else if (repeatMode === 'all') {
        // Repeat all: go to next track or first track
        if (currentIndex < playlist.length - 1) {
          setTimeout(() => playTrack(currentIndex + 1), 100);
        } else {
          setTimeout(() => playTrack(0), 100);
        }
      } else if (currentIndex < playlist.length - 1) {
        // No repeat: play next track in playlist
        setTimeout(() => playTrack(currentIndex + 1), 100);
      }
    };

    const handleError = (error) => {
      console.error('Audio playback error');
      setIsPlaying(false);
      setIsLoading(false);
    };

    audio.addEventListener('timeupdate', updateProgress);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('loadstart', handleLoadStart);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);

    return () => {
      audio.removeEventListener('timeupdate', updateProgress);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('loadstart', handleLoadStart);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
    };
  }, [currentIndex, playlist.length, currentStreamId, playTrack, queue, refreshListeningTime, repeatMode, streamStartTime]);

  // Handle session recovery when returning to the app
  const handleSessionRecovery = useCallback(() => {
    if (lastPlayedState?.track && !currentStreamId) {
      console.log('🔄 Session recovery: User returned to app with last played track');
      // The user can manually resume if they want to continue listening
      // We don't auto-resume as they might not want that
    }
  }, [lastPlayedState?.track, currentStreamId]);

  // Safely end listening session with retry logic
  const safelyEndListeningSession = useCallback(async (streamId, startTime, retryCount = 0) => {
    if (!streamId || !startTime) return;
    
    try {
      const token = storageSync.getItem('token');
      if (!token) return;
      
      const durationSeconds = Math.floor((Date.now() - startTime) / 1000);
      
      const response = await fetchStreamEnd(streamId, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${token}`,
        },
        body: JSON.stringify({
          duration_seconds: durationSeconds
        })
      });

      if (response.ok) {
        console.log('🔄 Successfully ended listening session, duration:', durationSeconds, 'seconds');
        return true;
      } else {
        console.error('🔄 Failed to end listening session:', response.status);
        if (retryCount < 2) {
          console.log(`🔄 Retrying to end listening session (attempt ${retryCount + 1})`);
          setTimeout(() => safelyEndListeningSession(streamId, startTime, retryCount + 1), 1000);
        }
        return false;
      }
    } catch (error) {
      console.error('🔄 Error ending listening session:', error);
      if (retryCount < 2) {
        console.log(`🔄 Retrying to end listening session (attempt ${retryCount + 1})`);
        setTimeout(() => safelyEndListeningSession(streamId, startTime, retryCount + 1), 1000);
      }
      return false;
    }
  }, []);

  // Handle page refresh, close, and navigation away from app
  useEffect(() => {
    const handleBeforeUnload = async (event) => {
      // End listening session if music is playing
      if (isPlaying && currentStreamId && streamStartTime) {
        try {
          const token = storageSync.getItem('token');
          if (token && !streamEndRequests.has(currentStreamId)) {
            streamEndRequests.set(currentStreamId, Promise.resolve(new Response(null)));
            // Use synchronous XMLHttpRequest for beforeunload (fetch doesn't work reliably here)
            const xhr = new XMLHttpRequest();
            xhr.open('PATCH', `${apiBaseURL}/music/track/listening/${currentStreamId}/end/`, false); // synchronous
            xhr.setRequestHeader('Content-Type', 'application/json');
            xhr.setRequestHeader('Authorization', `Token ${token}`);
            
            const durationSeconds = Math.floor((Date.now() - streamStartTime) / 1000);
            const requestBody = JSON.stringify({
              duration_seconds: durationSeconds
            });
            
            console.log('🔄 Page unloading - ending listening session, duration:', durationSeconds, 'seconds, stream_id:', currentStreamId);
            xhr.send(requestBody);
            
            if (xhr.status === 200) {
              console.log('🔄 Successfully ended listening session before page unload');
            } else {
              console.error('🔄 Failed to end listening session before page unload:', xhr.status);
            }
          }
        } catch (error) {
          console.error('🔄 Error ending listening session before page unload:', error);
        }
      }
    };

    const handleVisibilityChange = async () => {
      // Allow audio to continue playing in background
      // Media Session API will handle native controls
      if (document.hidden) {
        console.log('🔄 Tab hidden - audio will continue playing in background');
        // Don't pause - let audio continue playing
      } else {
        // Page/tab is visible again
        console.log('🔄 Tab visible again');
      }
    };

    const handlePageHide = () => {
      // Handle when page is hidden (navigation, tab switch, etc.)
      console.log('🔄 Page hidden event triggered');
      // This is a backup for browsers that don't support visibilitychange well
    };

    const handlePageShow = () => {
      // Handle when page becomes visible again
      console.log('🔄 Page show event triggered');
      // Check if we need to recover any sessions
      handleSessionRecovery();
    };

    // Add event listeners
    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('pageshow', handlePageShow);

    // Cleanup event listeners
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [isPlaying, currentStreamId, streamStartTime, handleSessionRecovery, safelyEndListeningSession]);

  // Cleanup pause timeout on unmount
  useEffect(() => {
    return () => {
      if (pauseTimeoutRef.current) {
        clearTimeout(pauseTimeoutRef.current);
      }
    };
  }, []);

  // Update volume when it changes
  useEffect(() => {
    audioRef.current.volume = volume;
  }, [volume]);

  const playPause = () => {
    if (!currentTrack) return;
    
    // Prevent rapid clicks - if already processing a pause, ignore
    if (isPausing && isPlaying) {
      return;
    }

    if (isPlaying) {
      pause(); // Call the actual pause function that handles end listening and Media Session state
    } else {
      // When resuming, we need to start a new listening session
      // but preserve the current audio position
      // Media Session state will be updated in resumeFromCurrentPosition
      resumeFromCurrentPosition();
    }
  };

  const pause = useCallback(async () => {
    // Prevent multiple rapid pause calls
    if (isPausing || !isPlaying) {
      return;
    }
    
    // Set flag immediately to prevent rapid clicks
    setIsPausing(true);
    
    // Update UI immediately - don't wait for API call
    audioRef.current.pause();
    setIsPlaying(false);
    
    // Clear playing state for credit balance polling optimization
    if (typeof window !== 'undefined') {
      localStorage.removeItem('musicPlayer_isPlaying');
    }
    
    // Update Media Session playback state immediately
    if ('mediaSession' in navigator) {
      navigator.mediaSession.playbackState = 'paused';
    }
    
    // Store values for async operation (they might change)
    const streamId = currentStreamId;
    const startTime = streamStartTime;
    
    // Clear stream tracking state immediately
    setCurrentStreamId(null);
    setStreamStartTime(null);
    
    // Reset pausing flag after a short delay to allow UI to update
    // This prevents rapid clicks but doesn't block the UI
    if (pauseTimeoutRef.current) {
      clearTimeout(pauseTimeoutRef.current);
    }
    pauseTimeoutRef.current = setTimeout(() => {
      setIsPausing(false);
    }, 100);
    
    // End listening tracking in background - don't block UI
    if (streamId && startTime) {
      // Fire and forget - don't await
      (async () => {
        try {
          const token = storageSync.getItem('token');
          if (token) {
            // Calculate duration in seconds
            const durationSeconds = Math.floor((Date.now() - startTime) / 1000);
            
            const response = await fetchStreamEnd(streamId, {
              method: 'PATCH',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Token ${token}`,
              },
              body: JSON.stringify({
                duration_seconds: durationSeconds
              })
            });
            
            if (!response.ok) {
              console.error('Failed to end listening session:', response.status);
            }
          }
        } catch (error) {
          console.error('Error ending listening tracking:', error);
        }
        
        // Trigger refresh of listening time data
        refreshListeningTime();
      })();
    }
  }, [isPlaying, isPausing, currentStreamId, streamStartTime, refreshListeningTime]);



  const seekTo = useCallback((time) => {
    if (audioRef.current) {
      // Handle scrubbing based on current state
      if (currentStreamId && streamStartTime) {
        // We have an active listening session - need to handle it properly
        const handleSeek = async () => {
          try {
            const token = storageSync.getItem('token');
            
            if (token) {
              // Calculate duration up to the seek point
              const durationSeconds = Math.floor((Date.now() - streamStartTime) / 1000);
              
              const response = await fetchStreamEnd(currentStreamId, {
                method: 'PATCH',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Token ${token}`,
                },
                body: JSON.stringify({
                  duration_seconds: durationSeconds
                })
              });
              
              if (response.ok) {
                // Clear current session - new one will start when play resumes
                setCurrentStreamId(null);
                setStreamStartTime(null);
              } else {
                console.error('Failed to end listening session before seek:', response.status);
              }
            }
          } catch (error) {
            console.error('Error ending listening session before seek:', error);
          }
        };
        
        handleSeek();
      }
      
      // Update audio position
      audioRef.current.currentTime = time;
      setProgress(time);
      
      // Note: New listening session will start when play resumes
      // This handles both pre-play scrubbing and paused scrubbing
    }
  }, [currentStreamId, streamStartTime]);



  // Every half minute while a track plays, tell the server how long this
  // session has been playing (pausing ends the session, so that's listening
  // time). A session nobody ends - a closed tab - is charged up to here.
  useEffect(() => {
    if (!isPlaying || !currentStreamId || !streamStartTime || String(currentStreamId).startsWith('offline:')) {
      return undefined;
    }
    const token = storageSync.getItem('token');
    if (!token) return undefined;
    const report = () => {
      const seconds = Math.floor((Date.now() - streamStartTime) / 1000);
      if (seconds <= 0) return;
      fetch(`${apiBaseURL}/music/track/listening/${currentStreamId}/progress/`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Token ${token}` },
        body: JSON.stringify({ duration_seconds: seconds }),
      }).catch(() => {
        // Offline for a moment: the next report or the session's end covers it
      });
    };
    const timer = setInterval(report, 30000);
    return () => clearInterval(timer);
  }, [isPlaying, currentStreamId, streamStartTime]);

  // Resume playback from last played position
  const resumeFromLastPosition = async (clickedPosition = null) => {
    if (lastPlayedState?.track) {
      const { track, playlist: savedPlaylist, currentIndex: savedIndex, progress: savedProgress } = lastPlayedState;
      
      if (track?.id) {
        // Start the listening session first: the stream address comes with
        // it (track.audio_file is only there for the track's owner)
        let sessionAudioUrl = null;
        try {
          const token = storageSync.getItem('token');
          if (token) {
            const response = await fetch(`${apiBaseURL}/music/track/listening/start/`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Token ${token}`,
              },
              body: JSON.stringify({ track_id: track.id }),
            });
            if (response.ok) {
              const data = await response.json();
              sessionAudioUrl = data.audio_url ? getAudioUrl(data.audio_url) : null;
              setCurrentStreamId(data.stream_id);
              setStreamStartTime(new Date());
            } else if (response.status === 402) {
              // Payment required - insufficient credit
              const errorData = await response.json();
              showCreditWarning(track.name, String(errorData.track_price_per_minute || '0.01'));
              return;
            }
          }
        } catch (error) {
          console.error('Error starting listening session for resume:', error);
        }

        const audioUrl = sessionAudioUrl || getAudioUrl(track.audio_file);
        if (!audioUrl) {
          console.error('No playable audio URL available to resume track:', track.id, track.name);
          alert('This track is not available for playback right now.');
          return;
        }

        // Set the playlist and current track
        setPlaylist(savedPlaylist || []);
        setCurrentTrack(track);
        setCurrentIndex(savedIndex || 0);

        audioRef.current.src = audioUrl;

        // Use clicked position if provided, otherwise use saved progress
        const startPosition = clickedPosition !== null ? clickedPosition : (savedProgress || 0);

        // Set the audio to the start position
        audioRef.current.currentTime = startPosition;
        setProgress(startPosition);

        // Now resume playback from the saved position
        const playPromise = audioRef.current.play();
        
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              setIsPlaying(true);
              setIsLoading(false);
              
              // Update Media Session playback state
              if ('mediaSession' in navigator) {
                navigator.mediaSession.playbackState = 'playing';
              }
            })
            .catch(error => {
              console.error('Failed to resume audio:', error);
              setIsPlaying(false);
              setIsLoading(false);
            });
        }
      }
    }
  };







  // Resume playback from current position (for play/pause cycles)
  const resumeFromCurrentPosition = useCallback(async () => {
    // Don't gate on currentTrack.audio_file - the catalog API only includes
    // that for the track's owner. The <audio> element's src was already set
    // from the real auth/credit-gated URL when playTrack() first ran, so all
    // we need here is a track to re-start the listening session for.
    if (!currentTrack?.id || !audioRef.current.src) {
      return;
    }
    
    // Start a new listening session for the current track
    if (currentTrack.id) {
      try {
        const token = storageSync.getItem('token');
        
        if (token) {
          const requestBody = { track_id: currentTrack.id };
          
          const response = await fetch(`${apiBaseURL}/music/track/listening/start/`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Token ${token}`,
            },
            body: JSON.stringify(requestBody),
          });
          
          if (response.ok) {
            const data = await response.json();
            setCurrentStreamId(data.stream_id);
            setStreamStartTime(new Date());
            
            // Now resume playback from current position
            setIsLoading(true);
            const playPromise = audioRef.current.play();
            
            if (playPromise !== undefined) {
              playPromise
                .then(() => {
                  setIsPlaying(true);
                  setIsLoading(false);
                  
                  // Update Media Session playback state
                  if ('mediaSession' in navigator) {
                    navigator.mediaSession.playbackState = 'playing';
                  }
                })
                .catch(error => {
                  console.error('Failed to resume audio:', error);
                  setIsPlaying(false);
                  setIsLoading(false);
                });
            }
          } else {
            if (response.status === 402) {
              // Payment required - insufficient credit
              const errorData = await response.json();
              showCreditWarning(currentTrack.name, String(errorData.track_price_per_minute || '0.01'));
              return;
            }
          }
        }
      } catch (error) {
        console.error('Error starting listening session for resume:', error);
      }
    }
  }, [currentTrack, showCreditWarning]);


  // Check if we need to recover a session on mount
  useEffect(() => {
    handleSessionRecovery();
  }, [handleSessionRecovery]);

  // Handle mobile app state changes (for mobile browsers)
  useEffect(() => {
    const handleAppStateChange = () => {
      if (document.hidden && isPlaying && currentStreamId && streamStartTime) {
        console.log('🔄 Mobile app going to background - pausing music and ending session');
        
        // Pause the audio
        audioRef.current.pause();
        setIsPlaying(false);
        
        // End the listening session
        safelyEndListeningSession(currentStreamId, streamStartTime).then(success => {
          if (success) {
            setCurrentStreamId(null);
            setStreamStartTime(null);
          }
        });
      }
    };

    // Add mobile-specific event listeners
    if ('onpagehide' in window) {
      window.addEventListener('pagehide', handleAppStateChange);
    }

    return () => {
      if ('onpagehide' in window) {
        window.removeEventListener('pagehide', handleAppStateChange);
      }
    };
  }, [isPlaying, currentStreamId, streamStartTime, safelyEndListeningSession]);

  // Clear play history
  const clearPlayHistory = () => {
    setPlayHistory([]);
    storageSync.removeItem('musicPlayer_playHistory');
  };


  // Close credit warning dialog
  const closeCreditWarning = () => {
    setCreditWarningDialog({
      open: false,
      trackName: '',
      requiredAmount: '0.01'
    });
  };

  // Note: handleAddCredit is now handled in the MusicPlayer component
  // to properly use React Router navigation

  // Get recent tracks from play history
  const getRecentTracks = (limit = 10) => {
    return playHistory.slice(0, limit);
  };







  // Get listening time for a track
  const getListeningTime = useCallback(async (trackId) => {
    try {
      const response = await fetch(`${baseURL}/api/music/track/${trackId}/listening-time/`);
      if (response.ok) {
        const data = await response.json();
        return data;
      } else {
        console.error('Failed to get listening time:', response.status);
        return null;
      }
    } catch (error) {
      console.error('Error getting listening time:', error);
      return null;
    }
  }, []);

  // Set playlist and start playing from a specific index
  const setPlaylistAndPlay = useCallback(async (newPlaylist, startIndex = 0, discoveryContext = {}) => {
    
    // Stop current playback if any
    if (isPlaying) {
      await pause();
    }
    
    if (discoveryContext && Object.keys(discoveryContext).length > 0) {
      newPlaylist = newPlaylist.map((t) => ({ ...t, discoveryContext }));
    }

    // Store original playlist for shuffle functionality
    setOriginalPlaylist([...newPlaylist]);
    
    // Apply shuffle if enabled
    let finalPlaylist = newPlaylist;
    let finalStartIndex = startIndex;
    
    if (isShuffleOn && newPlaylist.length > 1) {
      // Create shuffled copy
      const shuffledPlaylist = [...newPlaylist].sort(() => Math.random() - 0.5);
      
      // Find the current track in the shuffled playlist
      const currentTrack = newPlaylist[startIndex];
      const newIndex = shuffledPlaylist.findIndex(track => track.id === currentTrack.id);
      
      if (newIndex !== -1) {
        finalPlaylist = shuffledPlaylist;
        finalStartIndex = newIndex;
      }
    }
    
    // Set the new playlist
    setPlaylist(finalPlaylist);
    setCurrentIndex(finalStartIndex);
    
    // Start playing from the specified index
    // Pass the track object directly instead of index to avoid race condition with state update
    if (finalPlaylist.length > 0 && finalPlaylist[finalStartIndex]) {
      const trackToPlay = finalPlaylist[finalStartIndex];
      // Don't gate on audio_file here - the catalog API only includes that
      // for the track's owner. playTrack() itself resolves the real URL via
      // the auth/credit-gated endpoint and reports "not available" if that
      // genuinely fails, so always hand off to it rather than pre-filtering.
      await playTrack({
        ...trackToPlay,
        audio_file: trackToPlay.audio_file || trackToPlay.audioFile
      }, discoveryContext);
    }
  }, [isPlaying, pause, isShuffleOn, playTrack]);

  // Set volume for the audio player
  const setVolumeValue = useCallback((newVolume) => {
    const clampedVolume = Math.max(0, Math.min(1, newVolume));
    setVolume(clampedVolume);
    
    if (audioRef.current) {
      audioRef.current.volume = clampedVolume;
    }
    
    // Save to localStorage
    storageSync.setItem('musicPlayer_volume', clampedVolume.toString());
  }, []);

  // Queue management functions
  const addToQueue = useCallback((track) => {
    setQueue(prev => [...prev, track]);
    console.log('🎵 Added to queue:', track.name);
  }, []);

  const playNext = useCallback((track) => {
    setQueue(prev => [track, ...prev]);
    console.log('🎵 Added to play next:', track.name);
  }, []);

  const removeFromQueue = useCallback((trackId) => {
    setQueue(prev => prev.filter(track => track.id !== trackId));
  }, []);

  const clearQueue = useCallback(() => {
    setQueue([]);
  }, []);

  // Leaving listening mode ends playback outright: the session is billed up
  // to now, and the player empties rather than restoring this track later
  const stopPlayback = useCallback(async () => {
    await pause();
    setCurrentTrack(null);
    setPlaylist([]);
    setCurrentIndex(0);
    setQueue([]);
    setProgress(0);
    setLastPlayedState(null);
    storageSync.removeItem('musicPlayer_lastPlayed');
  }, [pause]);

  const getQueue = useCallback(() => {
    return queue;
  }, [queue]);

  // Enhanced next and previous functions that work with both playlist and queue
  const next = useCallback(() => {
    // First check if there are queued tracks
    if (queue.length > 0) {
      const nextTrack = queue[0];
      setQueue(prev => prev.slice(1)); // Remove the first track from queue
      playTrack(nextTrack);
      return;
    }
    
    // If no queued tracks, check playlist
    if (currentIndex < playlist.length - 1) {
      playTrack(currentIndex + 1);
    } else if (repeatMode === 'all' && playlist.length > 0) {
      // Repeat all: go to first track
      playTrack(0);
    }
  }, [queue, currentIndex, playlist.length, playTrack, repeatMode]);

  const previous = useCallback(() => {
    if (currentIndex > 0) {
      playTrack(currentIndex - 1);
    } else if (repeatMode === 'all' && playlist.length > 0) {
      // Repeat all: go to last track
      playTrack(playlist.length - 1);
    }
  }, [currentIndex, playTrack, repeatMode, playlist.length]);

  // Shuffle toggle function
  const toggleShuffle = useCallback(() => {
    const newShuffleState = !isShuffleOn;
    setIsShuffleOn(newShuffleState);
    
    if (newShuffleState && playlist.length > 1) {
      // Enable shuffle: create shuffled playlist
      const shuffledPlaylist = [...playlist].sort(() => Math.random() - 0.5);
      
      // Find current track in shuffled playlist
      const currentTrack = playlist[currentIndex];
      const newIndex = shuffledPlaylist.findIndex(track => track.id === currentTrack.id);
      
      if (newIndex !== -1) {
        setPlaylist(shuffledPlaylist);
        setCurrentIndex(newIndex);
      }
    } else if (!newShuffleState && originalPlaylist.length > 0) {
      // Disable shuffle: restore original playlist
      const currentTrack = playlist[currentIndex];
      const originalIndex = originalPlaylist.findIndex(track => track.id === currentTrack.id);
      
      if (originalIndex !== -1) {
        setPlaylist(originalPlaylist);
        setCurrentIndex(originalIndex);
      }
    }
  }, [isShuffleOn, playlist, currentIndex, originalPlaylist]);

  // Repeat toggle function
  const toggleRepeat = useCallback(() => {
    const modes = ['off', 'all', 'one'];
    const currentModeIndex = modes.indexOf(repeatMode);
    const nextModeIndex = (currentModeIndex + 1) % modes.length;
    setRepeatMode(modes[nextModeIndex]);
  }, [repeatMode]);

  // Clear invalid localStorage data (useful for debugging)
  const clearInvalidData = useCallback(() => {
    try {
      const savedLastPlayed = storageSync.getItem('musicPlayer_lastPlayed');
      if (savedLastPlayed) {
        const lastPlayed = JSON.parse(savedLastPlayed);
        if (!lastPlayed.track || !lastPlayed.track.id || !lastPlayed.track.name) {
          console.warn('🎵 Clearing invalid localStorage data');
          storageSync.removeItem('musicPlayer_lastPlayed');
          setLastPlayedState(null);
          setCurrentTrack(null);
        }
      }
    } catch (error) {
      console.error('🎵 Error clearing invalid data:', error);
      storageSync.removeItem('musicPlayer_lastPlayed');
      setLastPlayedState(null);
      setCurrentTrack(null);
    }
  }, []);

  // Media Session API - Set up action handlers (moved here after all functions are defined)
  useEffect(() => {
    if ('mediaSession' in navigator) {
      // Play action
      navigator.mediaSession.setActionHandler('play', () => {
        console.log('🎵 Media Session: Play action');
        if (!isPlaying && currentTrack) {
          resumeFromCurrentPosition();
        }
      });

      // Pause action
      navigator.mediaSession.setActionHandler('pause', () => {
        console.log('🎵 Media Session: Pause action');
        if (isPlaying) {
          pause();
        }
      });

      // Previous track action
      navigator.mediaSession.setActionHandler('previoustrack', () => {
        console.log('🎵 Media Session: Previous track action');
        previous();
      });

      // Next track action
      navigator.mediaSession.setActionHandler('nexttrack', () => {
        console.log('🎵 Media Session: Next track action');
        next();
      });

      // Seek backward action (optional)
      navigator.mediaSession.setActionHandler('seekbackward', (details) => {
        console.log('🎵 Media Session: Seek backward action', details);
        const seekTime = details.seekOffset || 10; // Default 10 seconds
        const newTime = Math.max(0, progress - seekTime);
        seekTo(newTime);
      });

      // Seek forward action (optional)
      navigator.mediaSession.setActionHandler('seekforward', (details) => {
        console.log('🎵 Media Session: Seek forward action', details);
        const seekTime = details.seekOffset || 10; // Default 10 seconds
        const newTime = Math.min(duration, progress + seekTime);
        seekTo(newTime);
      });

      // Seek to action (optional, for scrubbing)
      navigator.mediaSession.setActionHandler('seekto', (details) => {
        console.log('🎵 Media Session: Seek to action', details);
        if (details.seekTime !== undefined) {
          seekTo(details.seekTime);
        }
      });
    }
  }, [isPlaying, currentTrack, pause, next, previous, seekTo, progress, duration, resumeFromCurrentPosition]);

  const value = {
    ...streamingQualityState,
    currentTrack,
    isPlaying,
    progress,
    duration,
    volume,
    playlist,
    currentIndex,
    playHistory,
    lastPlayedState,
    isLoading,
    queue,
    isShuffleOn,
    repeatMode,
    creditWarningDialog,
    playTrack,
    playPause,
    pause,
    next,
    previous,
    seekTo,
    setVolume: setVolumeValue,
    setPlaylistAndPlay,
    addToQueue,
    playNext,
    removeFromQueue,
    clearQueue,
    stopPlayback,
    getQueue,
    toggleShuffle,
    toggleRepeat,
    resumeFromLastPosition,
    resumeFromCurrentPosition,
    handleSessionRecovery,
    safelyEndListeningSession,
    clearPlayHistory,
    getRecentTracks,
    getListeningTime,
    refreshListeningTime,
    listeningTimeRefresh,
    showCreditWarning,
    closeCreditWarning,
    clearInvalidData,
  };

  return (
    <MusicPlayerContext.Provider value={value}>
      {children}
    </MusicPlayerContext.Provider>
  );
};
